import React from 'react';
import { InfoIcon } from 'lucide-react';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { useDarkClasses } from '../../hooks/useDarkClasses';
export const Tooltip = ({
  content
}: {
  content: string;
}) => <div className="group relative flex" data-id="element-5848" data-mp="2nxm2yu8xx6hs">
    <InfoIcon className="h-4 w-4 text-gray-400 cursor-help" data-id="element-5849" data-mp="2nugijsb9asse" />
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-gray-800 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10" data-id="element-5850" data-mp="2ju6bsdfn3ych">
      {content}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" data-id="element-5851" data-mp="2stjmf9fi2sys"></div>
    </div>
  </div>;
export const BarChart = ({
  data,
  maxHeight = 150
}: {
  data: any[];
  maxHeight?: number;
}) => {
  const maxValue = Math.max(...data.map(item => item.count));
  const rt = useRoleTheme();
  const dk = useDarkClasses();
  return <div className="flex items-end space-x-2 h-40" data-id="element-5852" data-mp="3bngftbt9ev21">
      {data.map((item, index) => <div key={index} className="flex flex-col items-center flex-1" data-id="element-5853" data-mp="1hgvasu2yo6e3" data-mpi={index}>
          <div className={`w-full rounded-t hover:opacity-80 transition-all duration-200 ${rt.bgDark}`} style={{
        height: `${item.count / maxValue * maxHeight}px`
      }} data-id="element-5854" data-mp="1men67y2tean1" />
          <div className={`text-xs mt-1 ${dk.textMuted}`} data-id="element-5855" data-mp="1wdufa92inj4c">{item.day}</div>
        </div>)}
    </div>;
};
export const DropoffChart = ({
  data
}: {
  data: any[];
}) => {
  const maxValue = Math.max(...data.map(item => item.dropoffRate));
  const rt = useRoleTheme();
  const dk = useDarkClasses();
  return <div className="relative" data-id="element-5856" data-mp="2a4zjlogtwzl0">
      <div className={`flex items-end space-x-1 h-40 border-b border-l pt-5 ${dk.border}`} data-id="element-5857" data-mp="2a6tu08uh59au">
        {data.map((item, index) => <div key={index} className="flex flex-col items-center flex-1 relative group" data-id="element-5858" data-mp="2k3orcof0h3nj" data-mpi={index}>
            <div className={`w-full hover:opacity-80 transition-all duration-200 ${item.dropoffRate === 15 ? 'bg-red-500' : rt.bgDark}`} style={{
          height: `${item.dropoffRate / maxValue * 120}px`
        }} data-id="element-5859" data-mp="2o4s3p0xuohk0">
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 p-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity z-10 whitespace-nowrap" data-id="element-5860" data-mp="xsuba2niumqf">
                {item.dropoffRate}% drop-off at Chapter {item.chapter}
              </div>
            </div>
            <div className={`text-xs mt-1 ${dk.textMuted}`} data-id="element-5861" data-mp="12q3z5dv9iqc5">{item.chapter}</div>
          </div>)}
      </div>
      <div className={`absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs -translate-x-6 ${dk.textFaint}`} data-id="element-5862" data-mp="1gpvjdwcdh2yl">
        <span data-id="element-5863" data-mp="3ihrrevzbzuom">15%</span>
        <span data-id="element-5864" data-mp="3ih9r9o9jswbf">10%</span>
        <span data-id="element-5865" data-mp="3iijo6a4nzygh">5%</span>
        <span data-id="element-5866" data-mp="3ik4bbeongf4b">0%</span>
      </div>
      <div className={`text-xs text-center mt-2 ${dk.textMuted}`} data-id="element-5867" data-mp="3egy4o5xmklxb">
        Chapter Number
      </div>
    </div>;
};
export const HeatmapChart = () => {
  const engagementData = [{
    chapter: 1,
    engagement: 65
  }, {
    chapter: 2,
    engagement: 70
  }, {
    chapter: 3,
    engagement: 60
  }, {
    chapter: 4,
    engagement: 55
  }, {
    chapter: 5,
    engagement: 50
  }, {
    chapter: 6,
    engagement: 45
  }, {
    chapter: 7,
    engagement: 40
  }, {
    chapter: 8,
    engagement: 85
  }, {
    chapter: 9,
    engagement: 90
  }, {
    chapter: 10,
    engagement: 95
  }, {
    chapter: 11,
    engagement: 90
  }, {
    chapter: 12,
    engagement: 85
  }, {
    chapter: 13,
    engagement: 75
  }, {
    chapter: 14,
    engagement: 70
  }, {
    chapter: 15,
    engagement: 75
  }];
  const getColorClass = (value: number) => {
    if (value < 45) return 'bg-red-200';
    if (value < 60) return 'bg-orange-200';
    if (value < 75) return 'bg-yellow-200';
    if (value < 85) return 'bg-green-200';
    return 'bg-green-400';
  };
  return <div className="flex flex-col space-y-1" data-id="element-5868" data-mp="26056tkygd66d">
      {engagementData.map((item, index) => <div key={index} className="flex items-center space-x-2" data-id="element-5869" data-mp="20z7s5icniu9m" data-mpi={index}>
          <div className="w-8 text-xs text-right text-gray-600" data-id="element-5870" data-mp="1wzgrmmxbozrw">
            Ch {item.chapter}
          </div>
          <div className="flex-1 h-6 bg-gray-100 rounded-sm overflow-hidden flex items-center" data-id="element-5871" data-mp="1s048ac5nuau2">
            <div className={`h-full ${getColorClass(item.engagement)} transition-all duration-300`} style={{
          width: `${item.engagement}%`
        }} data-id="element-5872" data-mp="1n8uf51kwkpdf">
              <div className="px-2 text-xs text-gray-700 flex items-center h-full" data-id="element-5873" data-mp="1dc9awigpjian">
                {item.engagement}%
              </div>
            </div>
          </div>
        </div>)}
      <div className="flex justify-between text-xs text-gray-500 px-10 mt-2" data-id="element-5874" data-mp="14bb7a1a6lcsl">
        <span className="flex items-center" data-id="element-5875" data-mp="3fy4ydl1y9scl">
          <span className="inline-block w-3 h-3 bg-red-200 mr-1" data-id="element-5876" data-mp="3fxbz155bm54h"></span> Low
        </span>
        <span className="flex items-center" data-id="element-5877" data-mp="3kv51po7f0h00">
          <span className="inline-block w-3 h-3 bg-orange-200 mr-1" data-id="element-5878" data-mp="3oyjjokewz7g3"></span>{' '}
          Medium
        </span>
        <span className="flex items-center" data-id="element-5879" data-mp="3txwim3j284z1">
          <span className="inline-block w-3 h-3 bg-yellow-200 mr-1" data-id="element-5880" data-mp="3twuw92wlfw20"></span> Good
        </span>
        <span className="flex items-center" data-id="element-5881" data-mp="2x8owatam9ru2">
          <span className="inline-block w-3 h-3 bg-green-200 mr-1" data-id="element-5882" data-mp="318ftbeqql471"></span> High
        </span>
        <span className="flex items-center" data-id="element-5883" data-mp="367sgk0lisda5">
          <span className="inline-block w-3 h-3 bg-green-400 mr-1" data-id="element-5884" data-mp="3670imj4ga3gp"></span> Very
          High
        </span>
      </div>
    </div>;
};
export const DonutChart = ({
  data,
  title
}: {
  data: any[];
  title: string;
}) => {
  let cumulativePercentage = 0;
  return <div className="flex flex-col items-center" data-id="element-5885" data-mp="2ihy2xgtdmkou">
      <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-5886" data-mp="2igmz5gzxlcxq">{title}</h3>
      <div className="relative w-40 h-40" data-id="element-5887" data-mp="3lsbv3vxszs4z">
        <svg viewBox="0 0 100 100" className="w-full h-full" data-id="element-5888" data-mp="3lr0sw0k0asfz">
          {data.map((item, index) => {
          const startAngle = cumulativePercentage * 3.6;
          cumulativePercentage += item.percentage;
          const endAngle = cumulativePercentage * 3.6;
          const startRad = (startAngle - 90) * Math.PI / 180;
          const endRad = (endAngle - 90) * Math.PI / 180;
          const x1 = 50 + 40 * Math.cos(startRad);
          const y1 = 50 + 40 * Math.sin(startRad);
          const x2 = 50 + 40 * Math.cos(endRad);
          const y2 = 50 + 40 * Math.sin(endRad);
          const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
          const pathData = [`M 50 50`, `L ${x1} ${y1}`, `A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2}`, `Z`].join(' ');
          const colors = ['#818cf8', '#a78bfa', '#c084fc', '#e879f9'];
          return <path key={index} d={pathData} fill={colors[index % colors.length]} stroke="#fff" strokeWidth="1" data-id="element-5889" data-mp="umea7ne7a44n" data-mpi={index} />;
        })}
          <circle cx="50" cy="50" r="25" fill="white" data-id="element-5890" data-mp="kq18c28i4jyt" />
        </svg>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2" data-id="element-5891" data-mp="pom6zx83uvl5">
        {data.map((item, index) => {
        const colors = ['bg-indigo-400', 'bg-purple-400', 'bg-fuchsia-400', 'bg-pink-400'];
        return <div key={index} className="flex items-center text-xs" data-id="element-5892" data-mp="13nluv2nsn6g9" data-mpi={index}>
              <div className={`w-3 h-3 ${colors[index % colors.length]} mr-1 rounded-sm`} data-id="element-5893" data-mp="13l81qzcoc4bc"></div>
              <span data-id="element-5894" data-mp="1wc9nxkesb04d">
                {item.device || item.method}: {item.percentage}%
              </span>
            </div>;
      })}
      </div>
    </div>;
};